import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule } from '@angular/forms';

import { BlockTextComponent } from '../../common/block-text/block-text.component';
import { FieldEditorV2Component } from '../../base-template-editor-v2/field-editor-v2/field-editor-v2.component';
import { InlineEditV2Directive } from '../../base-template-editor-v2/inline-edit/inline-edit-v2.directive';
import { InlineMediaV2Directive } from '../../base-template-editor-v2/inline-edit/inline-media-v2.directive';
import { BaseComponent } from '../../common/BaseComponent';
import { Helper } from '../../../../utils/helper';
import {
  ReviewsInitModel,
  ReviewsInitVariables,
  ReviewsModel,
  ReviewsVariables,
} from '../../../../models/email-template/reviews.interface';

/**
 * Mockup-styled Reviews block - a drop-in replacement for app-reviews in
 * block-renderer-v2 (same [block]/[isPreview] inputs and outputs), so it
 * covers both the center-canvas summary (isPreview=true) and the
 * right-panel edit form (isPreview=false). Reuses the real, unmodified
 * app-block-text (image upload/segment/validation) and
 * app-field-editor-v2/app-pin sub-components and the exact same
 * ReviewsVariables field names - only the surrounding layout is new.
 * Property name renders above the image, matching v1's own preview order
 * exactly (the mockup's own live-render puts the image first, but v1's real
 * behavior - which this must preserve - shows the name first).
 */
@Component({
  selector: 'app-reviews-v2',
  imports: [CommonModule, ReactiveFormsModule, BlockTextComponent, FieldEditorV2Component, InlineEditV2Directive, InlineMediaV2Directive],
  templateUrl: './reviews-v2.component.html',
  styleUrl: './reviews-v2.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class ReviewsComponentV2 extends BaseComponent implements OnInit {
  @Input() block: ReviewsModel = new ReviewsInitModel();
  @Input() isPreview = false;
  @Input() disabled = false;

  @Output() blockData = new EventEmitter<any>();
  @Output() removeBlockEvent = new EventEmitter<string | null>();

  ngOnInit() {
    if (Helper.isEmpty(this.block.variables!)) {
      this.block.variables = new ReviewsInitVariables();
    }
  }

  imagePlaceholder = 'Image';
  imageHrefPlaceholder = 'Enter Image Redirect Link';
  imageAltPlaceholder = 'Enter Image Alt Text';
  propertyNamePlaceholder = 'Enter Property Name';
  textPlaceholder = 'Enter Text';

  protected imageAsUpload = true;

  protected toggleImageMode(): void {
    this.imageAsUpload = !this.imageAsUpload;
  }

  get previewHref(): string {
    const base = this.helper.getFullUrl(this.block.variables?.['ReviewsImageHref']);
    const segment = this.block.variables?.['ReviewsImageHrefSegmentRegex'];
    if (!segment) {
      return base;
    }
    const separator = base.includes('?') ? '&' : '?';
    return `${base}${separator}segment=${encodeURIComponent(segment)}`;
  }

  removeBlock(): void {
    this.removeBlockEvent.emit(this.block.blockId);
  }

  onChange(value: any, fieldName: keyof ReviewsVariables) {
    if (this.block.variables) {
      (this.block.variables as any)[fieldName] = value;
      this.blockData.emit(this.block);
    }
  }


  handleOnSegmentChange(segment: string, blockName: string) {
    if (this.block.variables) {
      (this.block.variables as any)[blockName] = segment;
      this.blockData.emit(this.block);
    }
  }
}
